import { activateShadows } from './accrual.js';
import { noteClosedAccruals, noteUnclaimed, snapshotClaims } from './claims.js';
import { addYears, NEVER } from './dates.js';
import { expiredCapitalized, nextDerivedExpiry, settleDerived, settleJudged, writeOffDerived, writeOffFixedDerived, } from './derived.js';
import { applyWaiver, bar, close, expire, extend, renew, } from './ledger.js';
export const applyWaivers = (caseState) => {
    for (const entry of caseState.interestEntries) {
        if (entry.ledger.waiver > 0) {
            settleDerived(entry);
            applyWaiver(entry.ledger);
        }
    }
};
const closeWithDependents = (entry, reason, ownWrittenOff) => {
    if (ownWrittenOff === 0 || entry.kind !== 'debt') {
        return;
    }
    for (const dependent of entry.dependents) {
        if (dependent.ledger !== null) {
            settleDerived(dependent);
            close(dependent.ledger, reason);
        }
    }
};
// The instant limitation is judged at: frozen in projections.
const limitationAt = (caseState, time) => Math.min(time, caseState.options.limitationFrozenAt ?? time);
// Whether any ledger's next expiry has come at `at`.
const anyOverdue = (caseState, at) => caseState.ledgerEntries.some((entry) => at >= entry.ledger.nextExpiry);
const bookJudgedAndFindOverdue = (caseState, time) => {
    const at = limitationAt(caseState, time);
    for (const entry of caseState.interestEntries) {
        settleJudged(entry, at);
    }
    return anyOverdue(caseState, at);
};
const expireAll = (caseState, time) => {
    const at = limitationAt(caseState, time);
    for (const entry of caseState.ledgerEntries) {
        const ledger = entry.ledger;
        if (at < ledger.nextExpiry) {
            continue;
        }
        if (entry.kind === 'debt' && !entry.clock.marked) {
            bar(ledger);
            continue;
        }
        // A day that derived write-offs took in full has nothing of its own left
        // to expire, however long its write-offs waited to be booked.
        settleJudged(entry, at);
        const expired = expire(ledger, at);
        closeWithDependents(entry, 'expired', expired);
        if (entry.kind === 'interest' && entry.interest.compoundingType !== null) {
            writeOffDerived(caseState, entry, expiredCapitalized(entry), at);
        }
        if (expired > 0 && entry.kind === 'debt' && entry.compoundedIn.length > 0) {
            writeOffFixedDerived(caseState, entry, expired, at);
        }
    }
};
export const extendAll = (caseState, at, basis) => {
    for (const entry of caseState.ledgerEntries) {
        // What a derived write-off took is not open, so is not extended.
        settleDerived(entry);
        extend(entry.ledger, at, basis);
    }
};
// A document extending limitation renews debt whose date passed unmarked, as
// on engine 1. Payments do not.
const renewDebt = (caseState, at) => {
    for (const entry of caseState.ledgerEntries) {
        if (entry.kind === 'debt' && !entry.clock.marked) {
            renew(entry.ledger, at);
        }
    }
};
export const nextInternalStop = (caseState, after) => {
    let next = NEVER;
    const foundation = caseState.foundations[caseState.nextFoundation];
    if (foundation !== undefined) {
        next = Math.min(next, foundation);
    }
    const irrecoverable = caseState.irrecoverable[caseState.nextIrrecoverable];
    if (irrecoverable !== undefined) {
        next = Math.min(next, irrecoverable.at);
    }
    const marked = caseState.marked[caseState.nextMarked];
    if (marked !== undefined) {
        next = Math.min(next, marked.at);
    }
    for (const entry of caseState.ledgerEntries) {
        // Compound interest and what it compounds on: what expires leaves the
        // base that day (D30). Covered interest: what expires makes room for
        // newer interest that day (D31).
        const expiryMatters = entry.dependents.length > 0 ||
            entry.interest?.compoundingType != null ||
            entry.compounds ||
            entry.ceilingCovered;
        if (!expiryMatters) {
            continue;
        }
        const ledger = entry.ledger;
        if (ledger.nextExpiry > after) {
            next = Math.min(next, ledger.nextExpiry);
        }
        // Nor can interest accrued from here on expire unseen.
        if (entry.kind === 'interest') {
            next = Math.min(next, addYears(after, ledger.periods.ordinary) + 1);
        }
        if (entry.kind === 'interest' && entry.interest.dirty) {
            next = Math.min(next, nextDerivedExpiry(caseState, entry, after));
        }
    }
    return next;
};
export const processStop = (caseState, at) => {
    noteClosedAccruals(caseState);
    const irrecoverableDue = (caseState.irrecoverable[caseState.nextIrrecoverable]?.at ?? NEVER) <= at;
    const markedDue = (caseState.marked[caseState.nextMarked]?.at ?? NEVER) <= at;
    // Corrections take the oldest accrual before any of it can expire or
    // become irrecoverable.
    if (irrecoverableDue || markedDue || anyOverdue(caseState, at)) {
        applyWaivers(caseState);
    }
    // What is claimable matters only to what is written off here.
    const writesOff = irrecoverableDue ||
        markedDue ||
        anyOverdue(caseState, limitationAt(caseState, at));
    const claimSnapshot = writesOff ? snapshotClaims(caseState) : null;
    expireAll(caseState, at);
    // Derived write-offs on the days whose limitation can pass here are booked
    // now. Booking can give an øre back to a day past its limitation; it
    // expires now, not at a later stop.
    while (bookJudgedAndFindOverdue(caseState, at)) {
        expireAll(caseState, at);
    }
    const { marked } = caseState;
    while (caseState.nextMarked < marked.length &&
        marked[caseState.nextMarked].at <= at) {
        const { entry } = marked[caseState.nextMarked++];
        settleDerived(entry);
        close(entry.ledger, 'expired');
    }
    noteUnclaimed(claimSnapshot, 'expired');
    // Reinstated interest that has not expired is debt from its voiding day,
    // so that day's events extend and pay it.
    activateShadows(caseState, at);
    const { foundations, irrecoverable } = caseState;
    while (caseState.nextFoundation < foundations.length &&
        foundations[caseState.nextFoundation] <= at) {
        const foundation = foundations[caseState.nextFoundation++];
        renewDebt(caseState, foundation);
        extendAll(caseState, foundation, 'foundation');
    }
    while (caseState.nextIrrecoverable < irrecoverable.length &&
        irrecoverable[caseState.nextIrrecoverable].at <= at) {
        const { entry } = irrecoverable[caseState.nextIrrecoverable++];
        settleDerived(entry);
        closeWithDependents(entry, 'irrecoverable', close(entry.ledger, 'irrecoverable'));
    }
    noteUnclaimed(claimSnapshot, 'irrecoverable');
};
//# sourceMappingURL=limitation.js.map